import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, UserCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

const links = [
    { to: "/", label: "Hem" },
    { to: "/om-oss", label: "Om oss" },
    { to: "/tjanster", label: "Rättsområden" },
    { to: "/vardnadstvist", label: "Vårdnadstvist", highlight: true },
    { to: "/paket", label: "Online" },
    { to: "/notarius-publicus", label: "Notarius Publicus" },
    { to: "/rattshjalp", label: "Rättshjälp" },
    { to: "/medarbetare", label: "Medarbetare" },
    { to: "/nyheter", label: "Nyheter" },
    { to: "/kontakt", label: "Kontakt" },
];

export default function Nav() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();
    const { user } = useAuth();
    const portalPath = user?.role === "admin" ? "/admin" : "/portal";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 backdrop-blur-xl transition-colors duration-500 ${
                scrolled
                    ? "bg-black/80 border-b border-white/10"
                    : "bg-black/40 border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    <Link
                        to="/"
                        data-testid="nav-logo"
                        className="flex items-center gap-3 group"
                    >
                        <span className="w-9 h-9 border border-[#C4A977]/60 grid place-items-center font-serif text-[#C4A977] text-lg leading-none">
                            BM
                        </span>
                        <span className="hidden sm:flex flex-col leading-none">
                            <span className="font-serif text-xl text-[#F4F4F5]">
                                BM Juridik
                            </span>
                            <span className="overline mt-1 !text-[0.6rem] !tracking-[0.3em]">
                                Juristbyrå
                            </span>
                        </span>
                    </Link>

                    <nav className="hidden xl:flex items-center gap-0.5">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`nav-link-${l.to.replace(/\//g, "") || "hem"}`}
                                className={({ isActive }) =>
                                    `px-3 py-2 text-sm tracking-wide transition-colors duration-300 inline-flex items-center gap-1.5 whitespace-nowrap ${
                                        isActive
                                            ? "text-[#C4A977]"
                                            : "text-[#A1A1AA] hover:text-[#F4F4F5]"
                                    }`
                                }
                            >
                                {l.label}
                                {l.highlight && (
                                    <span
                                        className="w-1.5 h-1.5 rounded-full bg-[#C4A977] animate-pulse"
                                        aria-label="Kostnadsfri rådgivning"
                                    />
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        {user ? (
                            <Link
                                to={portalPath}
                                data-testid="nav-portal-link"
                                className="hidden md:inline-flex items-center gap-2 px-4 py-2 text-sm border border-[#C4A977]/40 text-[#C4A977] hover:bg-[#C4A977]/10 rounded-sm transition-colors"
                            >
                                <UserCircle2 size={14} />
                                {user.role === "admin" ? "Admin" : "Min portal"}
                            </Link>
                        ) : (
                            <Link
                                to="/logga-in"
                                data-testid="nav-login-link"
                                className="hidden md:inline-flex items-center gap-2 px-4 py-2 text-sm text-[#A1A1AA] hover:text-[#C4A977] transition-colors"
                            >
                                Logga in
                            </Link>
                        )}
                        <Link
                            to="/kontakt"
                            data-testid="nav-cta-kontakt"
                            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-sm bg-[#C4A977] text-black hover:bg-[#DBC08F] transition-colors duration-300 rounded-sm font-medium"
                        >
                            Boka konsultation
                        </Link>
                        <button
                            data-testid="nav-mobile-toggle"
                            onClick={() => setOpen((v) => !v)}
                            className="xl:hidden p-2 text-[#F4F4F5] hover:text-[#C4A977] transition-colors"
                            aria-label="Meny"
                        >
                            {open ? <X size={22} /> : <Menu size={22} />}
                        </button>
                    </div>
                </div>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-panel"
                    className="xl:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl"
                >
                    <div className="px-4 py-6 space-y-1">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`nav-mobile-link-${l.to.replace(/\//g, "") || "hem"}`}
                                className={({ isActive }) =>
                                    `flex items-center justify-between px-4 py-3 text-base border-l-2 ${
                                        isActive
                                            ? "text-[#C4A977] border-[#C4A977]"
                                            : "text-[#A1A1AA] border-transparent hover:text-[#F4F4F5]"
                                    } transition-colors`
                                }
                            >
                                <span>{l.label}</span>
                                {l.highlight && (
                                    <span className="text-[10px] tracking-[0.15em] uppercase text-[#C4A977] font-semibold border border-[#C4A977]/40 px-2 py-0.5 rounded-sm">
                                        Gratis
                                    </span>
                                )}
                            </NavLink>
                        ))}
                        <Link
                            to="/kontakt"
                            data-testid="nav-mobile-cta"
                            className="mt-4 inline-flex items-center px-5 py-3 text-sm bg-[#C4A977] text-black rounded-sm w-full justify-center font-medium"
                        >
                            Boka konsultation
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
